import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, Sparkles, ArrowRight, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney, fmtPct, displayTicker, marginColor } from "../lib/format";

const SCOPE_LABELS = {
  both: "Universe + Portfolio",
  universe: "My Universe only",
  portfolio: "My Stocks only",
};

function MarketStatusPill() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const day = now.getUTCDay();
      if (day === 0 || day === 6) {
        setOpen(false);
        return;
      }
      const m = now.getUTCHours() * 60 + now.getUTCMinutes();
      setOpen(m >= 3 * 60 + 45 && m <= 10 * 60);
    };
    check();
    const id = setInterval(check, 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
        open
          ? "text-moss bg-moss-bg/60 border-moss/30"
          : "text-stone_brand-light bg-bone-100 border-line"
      }`}
      data-testid="market-status-pill"
      title={open ? "Auto-refreshes every 5 minutes" : "Auto-refresh paused outside market hours"}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${open ? "bg-moss animate-pulse" : "bg-stone_brand-light"}`}
      />
      {open ? "Live · NSE open" : "NSE closed"}
    </span>
  );
}

export default function DailyPicksWidget({ scope = "both", limit = 6 }) {
  const [picks, setPicks] = useState([]);
  const [meta, setMeta] = useState({ scanned: 0, total_undervalued_down: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(
        `/picks/today?scope=${scope}&limit=${limit}`,
        { timeout: 90000 }
      );
      setPicks(data.picks || []);
      setMeta({
        scanned: data.scanned,
        total_undervalued_down: data.total_undervalued_down,
        generated_at: data.generated_at,
      });
    } catch (_e) {
      setPicks([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-poll every 5 min during Indian market hours (Mon–Fri, 09:15–15:30 IST)
    const tick = () => {
      const now = new Date();
      const day = now.getUTCDay(); // 0=Sun, 6=Sat
      if (day === 0 || day === 6) return;
      // IST = UTC+5:30 → market window in UTC: 03:45 → 10:00
      const utcMin = now.getUTCHours() * 60 + now.getUTCMinutes();
      const open = 3 * 60 + 45;
      const close = 10 * 60;
      if (utcMin >= open && utcMin <= close) load();
    };
    const id = setInterval(tick, 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, limit]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  return (
    <section
      className="border border-line bg-white rounded-sm overflow-hidden mb-8"
      data-testid="daily-picks-widget"
    >
      <header className="px-5 py-4 border-b border-line flex items-start justify-between gap-3 bg-gradient-to-br from-moss-bg/50 via-white to-amber_brand-bg/20">
        <div>
          <div className="overline mb-1 flex items-center gap-1.5 text-moss">
            <Sparkles className="w-3 h-3" />— Today's BUY radar
          </div>
          <div className="font-serif-display text-2xl text-stone_brand-dark font-medium tracking-tight">
            Biggest falls on{" "}
            <span className="text-moss">undervalued</span> stocks
          </div>
          <div className="text-xs text-stone_brand mt-1 flex items-center gap-2 flex-wrap">
            <span>
              Scanned <span className="font-mono">{meta.scanned}</span> tickers · {meta.total_undervalued_down}{" "}
              currently down + still trading below fair value · {SCOPE_LABELS[scope]}
            </span>
            <MarketStatusPill />
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-1.5 text-stone_brand-light hover:text-moss hover:bg-bone-100 rounded-sm transition-colors flex-shrink-0"
          data-testid="daily-picks-refresh"
          title="Refresh picks"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </header>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-line">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-4 h-28 animate-pulse" />
          ))}
        </div>
      ) : picks.length === 0 ? (
        <div className="p-10 text-center" data-testid="daily-picks-empty">
          <TrendingDown className="w-7 h-7 text-stone_brand-light mx-auto mb-3" />
          <div className="font-serif-display text-lg text-stone_brand-dark mb-1">
            Nothing falling + undervalued today
          </div>
          <p className="text-xs text-stone_brand max-w-sm mx-auto">
            Either the market is up across your watchlist, or your undervalued names are
            holding steady. Check back during market hours for fresh opportunities.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line">
          {picks.map((p, idx) => (
            <li
              key={p.ticker}
              className="bg-white p-4 hover:bg-bone-100 transition-colors"
              data-testid={`pick-${p.ticker}`}
            >
              <Link
                to={`/stock/${encodeURIComponent(p.ticker)}`}
                className="block group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[0.65rem] uppercase tracking-widest text-stone_brand-light w-5 text-right">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="font-mono text-sm font-semibold text-stone_brand-dark group-hover:text-moss transition-colors">
                        {displayTicker(p.ticker)}
                      </span>
                      <div className="flex items-center gap-1">
                        {p.in_portfolio && (
                          <span className="text-[0.55rem] font-mono uppercase tracking-widest text-amber_brand bg-amber_brand-bg border border-amber_brand/30 px-1.5 py-0.5 rounded-sm">
                            owned
                          </span>
                        )}
                        {p.in_universe && !p.in_portfolio && (
                          <span className="text-[0.55rem] font-mono uppercase tracking-widest text-moss border border-moss/30 bg-moss-bg/50 px-1.5 py-0.5 rounded-sm">
                            watching
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[0.7rem] text-stone_brand truncate mt-0.5 pl-7">
                      {p.company_name}
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-stone_brand-light group-hover:text-moss group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>

                <div className="grid grid-cols-3 gap-2 pl-7">
                  <div>
                    <div className="text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light mb-0.5">
                      Live
                    </div>
                    <div className="font-mono text-sm text-stone_brand-dark">
                      {fmtMoney(p.current_price, p.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light mb-0.5">
                      Today
                    </div>
                    <div className={`font-mono text-sm font-semibold ${marginColor(p.day_change_percent)}`}>
                      {fmtPct(p.day_change_percent)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[0.55rem] font-mono uppercase tracking-widest text-moss mb-0.5">
                      to FV
                    </div>
                    <div className="font-mono text-sm font-semibold text-moss">
                      {fmtPct(p.fv_diff_percent)}
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {picks.length > 0 && (
        <div className="px-5 py-2 border-t border-line bg-bone-50">
          <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">
            — Ranked by composite of intraday fall + upside to fair value. Auto-refreshes every 5 min during NSE hours.
          </p>
        </div>
      )}
    </section>
  );
}
